import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import * as signalR from '@microsoft/signalr';
import { PUBLIC_API_ENDPOINT } from '../../constants/platform';
import TopDonateBoard from '../../components/top-donate/top-donate-board';
import {
  TopDonateRow,
  loadTopDonate,
  parseTopDonateOptions,
  topDonateTitle,
} from '../../components/top-donate/top-donate-options';

/**
 * Overlay BẢNG XẾP HẠNG DONATE — chèn vào OBS bằng Browser Source, không cần đăng nhập.
 *
 * Tuỳ chọn đọc từ link (?range=&by=&top=&style=). Nghe cùng hub SignalR với overlay thông báo
 * donate: mỗi lần có donate thì tải lại bảng từ máy chủ (không tự cộng ở client, tránh lệch khi
 * rớt mạng). Cứ 60 giây tải lại một lần để qua 0 giờ thì "hôm nay" tự sang ngày mới.
 */
const REFRESH_MS = 60000;

const PageTopDonateLivestream = () => {
  const { contentCreatorId } = useParams<{ contentCreatorId: string }>();
  const [searchParams] = useSearchParams();
  const query = searchParams.toString();
  const options = useMemo(() => parseTopDonateOptions(new URLSearchParams(query)), [query]);

  const [rows, setRows] = useState<TopDonateRow[] | null>(null);
  const [donateTick, setDonateTick] = useState(0);
  // Lượt tải do donate và lượt tải định kỳ có thể về lệch thứ tự: chỉ nhận kết quả của lượt gọi sau cùng.
  const seqRef = useRef(0);

  const load = useCallback(async () => {
    if (!contentCreatorId) return;
    const seq = ++seqRef.current;
    try {
      const next = await loadTopDonate(contentCreatorId, options);
      if (seq === seqRef.current) setRows(next);
    } catch {
      // Lỗi mạng: giữ nguyên bảng đang hiện, lần sau sẽ tải lại.
    }
  }, [contentCreatorId, options]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // OBS tự làm nền trong suốt, nhưng mở thử bằng trình duyệt thường thì nền app sẽ che mất.
  useEffect(() => {
    const { body, documentElement } = document;
    const prevBody = body.style.background;
    const prevHtml = documentElement.style.background;
    body.style.background = 'transparent';
    documentElement.style.background = 'transparent';
    return () => {
      body.style.background = prevBody;
      documentElement.style.background = prevHtml;
    };
  }, []);

  useEffect(() => {
    if (!contentCreatorId) return undefined;
    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${PUBLIC_API_ENDPOINT}/payment-hubs?userid=${contentCreatorId}`, {
        transport: signalR.HttpTransportType.WebSockets,
        skipNegotiation: true,
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connection
      .start()
      .then(() => {
        connection.on('ReceiveDonationInfo', () => {
          load();
          setDonateTick((n) => n + 1);
        });
      })
      .catch(() => {
        // Không nối được hub: vẫn hiện bảng ở lần tải đầu và tải lại định kỳ.
      });

    return () => {
      connection.stop();
    };
  }, [contentCreatorId, load]);

  // Chưa tải xong lần đầu: không vẽ gì để OBS trong suốt, tránh nháy bảng trống.
  if (rows === null) return null;

  return (
    <div className="flex w-full items-start justify-center bg-transparent p-4">
      <div className="w-full max-w-[420px]">
        <TopDonateBoard
          title={topDonateTitle(options.range)}
          rows={rows}
          by={options.by}
          style={options.style}
          animateKey={donateTick}
        />
      </div>
    </div>
  );
};

export default PageTopDonateLivestream;
